import * as B from '@blueprintjs/core';
import React from 'react';

import {getPackageOrderText} from 'app/components/OrderImageryModal/orderImageryUtils';
import {SceneDetails, ScenePreviewGuidance} from 'app/components/SceneDetails';
import {ScenePreviewControls, ScenePreviewStage} from 'app/components/ScenePreviewStepper';
import {
  CartItemV1,
  SnapshotThumbnail,
  cartItemSubsetFeature,
  formatAcresLabel,
  formatItemPrice,
  isPartialAoiItem,
  sceneMetaParts,
} from 'app/modules/Cart';
import * as featureUtils from 'app/utils/featureUtils';
import {getScenePreviewKind} from 'app/utils/imageryUtils';

import type {CartItemPreview} from '../types';
import cs from './styles.module.scss';

const STAGE_SIZE = 480;

export interface CartPreviewLightboxProps {
  /** Every cart item in the order the page shows them. */
  items: CartItemV1[];
  previews: ReadonlyMap<string, CartItemPreview>;
  /** Null keeps the lightbox closed. */
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  onRemove: (item: CartItemV1) => void;
  isBusy?: boolean;
}

type PreviewKind = ReturnType<typeof getScenePreviewKind>;

const ItemDetails: React.FC<{
  item: CartItemV1;
  preview: CartItemPreview | undefined;
  kind: PreviewKind | undefined;
  showPreview: boolean;
}> = ({item, preview, kind, showPreview}) => {
  const isPartialScene = React.useMemo(() => {
    const coverage = preview
      ? featureUtils.getSceneCoverageOrNull(preview.scene, preview.feature)
      : null;
    return coverage !== null && coverage < 1;
  }, [preview]);

  return (
    <>
      <div>
        <div className={cs.featureName}>{item.snapshot.featureName}</div>
        <div className={cs.meta}>{item.snapshot.projectName}</div>
      </div>

      {preview && kind?.isPackage ? (
        <div className={cs.packageText}>{getPackageOrderText(kind.layerKey)}</div>
      ) : preview ? (
        <SceneDetails scene={preview.scene} />
      ) : (
        // The refresh pass hasn't produced the scene yet (or the feature is gone), so the row's
        // stored snapshot is all there is to say.
        <div className={cs.meta}>{sceneMetaParts(item).filter(Boolean).join(' · ')}</div>
      )}

      {showPreview && (
        <ScenePreviewGuidance className={cs.guidance} isPartialScene={isPartialScene} />
      )}

      <div className={cs.order}>
        {isPartialAoiItem(item) && (
          <B.Tag minimal round>
            Partial area
          </B.Tag>
        )}
        <span>{formatAcresLabel(item.billableAcres)}</span>
        <span aria-hidden="true">·</span>
        <span className={cs.price}>{formatItemPrice(item)}</span>
      </div>
    </>
  );
};

/** Steps through every cart item's preview at a size where clouds, haze and colour can be judged. */
export const CartPreviewLightbox: React.FC<CartPreviewLightboxProps> = ({
  items,
  previews,
  index,
  onIndexChange,
  onClose,
  onRemove,
  isBusy,
}) => {
  const isOpen = index !== null && items.length > 0;
  // Removing the last item in view leaves the index past the end, so it clamps rather than closes.
  const current = index === null ? 0 : Math.min(index, items.length - 1);
  const item = items[current];
  const preview = item ? previews.get(item.id) : undefined;
  const kind = preview ? getScenePreviewKind(preview.scene) : undefined;
  // As in the order modal: a scene with no picture gets none, unless a drawn area needs showing.
  const showPreview = (kind?.hasPreviewImage ?? true) || item?.geometry != null;

  // Global rather than on an element: the dialog opens with focus on Blueprint's own focus-trap
  // node, outside anything rendered here, so an element handler wouldn't hear the keys until
  // something inside it was clicked.
  const hotkeys = React.useMemo<B.HotkeyConfig[]>(
    () => [
      {
        combo: 'left',
        global: true,
        preventDefault: true,
        disabled: !isOpen || current <= 0,
        label: 'Previous cart item',
        onKeyDown: () => onIndexChange(current - 1),
      },
      {
        combo: 'right',
        global: true,
        preventDefault: true,
        disabled: !isOpen || current >= items.length - 1,
        label: 'Next cart item',
        onKeyDown: () => onIndexChange(current + 1),
      },
    ],
    [isOpen, current, items.length, onIndexChange]
  );
  B.useHotkeys(hotkeys);

  return (
    <B.Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Review imagery"
      icon="media"
      className={cs.dialog}
      canOutsideClickClose
    >
      {item && (
        <div>
          <div className={cs.body}>
            {showPreview && (
              <ScenePreviewStage
                className={cs.stage}
                size={STAGE_SIZE}
                source={
                  preview
                    ? {
                        feature: preview.feature,
                        scene: preview.scene,
                        orderableScenes: preview.orderableScenes,
                        subsetFeature: cartItemSubsetFeature(item),
                      }
                    : null
                }
                fallback={
                  <SnapshotThumbnail
                    className={cs.fallbackImage}
                    url={item.snapshot.thumbnailUrl}
                    isOverride={item.snapshot.thumbnailIsOverride}
                    alt={`${item.snapshot.featureName} preview`}
                    placeholderSize={48}
                  />
                }
              />
            )}
            <div className={cs.details}>
              <ItemDetails item={item} preview={preview} kind={kind} showPreview={showPreview} />
              <div className={cs.actions}>
                <B.Button
                  icon="cross"
                  text="Remove from cart"
                  disabled={isBusy}
                  onClick={() => onRemove(item)}
                />
              </div>
            </div>
          </div>
          <div className={cs.footer}>
            <ScenePreviewControls
              count={items.length}
              index={current}
              onIndexChange={onIndexChange}
            />
          </div>
        </div>
      )}
    </B.Dialog>
  );
};

export default CartPreviewLightbox;
